import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { History as HistoryIcon, CheckCircle2, XCircle, Target, Trophy, Trash2, Share2, Link as LinkIcon, MessageCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { Calendar, Copy, Check } from "lucide-react";

export default function History() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [predictionToDelete, setPredictionToDelete] = useState<number | null>(null);
  const [copiedDateCombosId, setCopiedDateCombosId] = useState<number | null>(null);
  const [expandedDateCombosId, setExpandedDateCombosId] = useState<number | null>(null);

  const handleCopyDateCombinations = (predId: number, combos: any[]) => {
    const allNumbers = combos.map((c: any) => c.number).join("-");
    navigator.clipboard.writeText(allNumbers);
    setCopiedDateCombosId(predId);
    toast.success("Date combinations copied!");
    setTimeout(() => setCopiedDateCombosId(null), 2000);
  };
  
  const utils = trpc.useUtils();
  
  // Get predictions with results
  const { data: predictionsWithResults, isLoading, error: loadError } = trpc.aiLearning.getUserPredictionResults.useQuery(
    { limit: 50 },
    { 
      enabled: isAuthenticated,
      retry: 1,
      staleTime: 30000, // 30 seconds
    }
  );

  // Get latest lottery draw
  const { data: latestDrawData } = trpc.lottery.getRecentDraws.useQuery(
    { limit: 1 },
    { enabled: isAuthenticated }
  );

  // Delete mutation
  const deleteMutation = trpc.prediction.deletePrediction.useMutation({
    onSuccess: () => {
      toast.success("Prediction deleted successfully");
      utils.aiLearning.getUserPredictionResults.invalidate();
      setDeleteDialogOpen(false);
      setPredictionToDelete(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete prediction: ${error.message}`);
    },
  });

  const latestDraw = latestDrawData?.[0];

  // Format numbers for sharing: "1247-6538-6484-6383"
  const formatNumbersForShare = (numbers: string[]) => {
    return numbers.join("-");
  };

  // Generate share message
  const generateShareMessage = (numbers: string[], date: string) => {
    const formattedNumbers = formatNumbersForShare(numbers);
    return `🎯 My Wega di Number AI Predictions for ${date}:\n\n${formattedNumbers}\n\nGenerated by AI at https://weganumber.suryc.space`;
  };

  // Share handlers
  const handleCopyLink = (numbers: string[], date: string) => {
    const message = generateShareMessage(numbers, date);
    navigator.clipboard.writeText(message);
    toast.success("Prediction copied to clipboard!");
  };

  const handleShareWhatsApp = (numbers: string[], date: string) => {
    const message = generateShareMessage(numbers, date);
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleShareFacebook = (numbers: string[], date: string) => {
    const message = generateShareMessage(numbers, date);
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://weganumber.suryc.space")}&quote=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleShareTwitter = (numbers: string[], date: string) => {
    const message = generateShareMessage(numbers, date);
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleDelete = (predictionId: number) => {
    setPredictionToDelete(predictionId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (predictionToDelete) {
      deleteMutation.mutate({ predictionId: predictionToDelete });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
        <Navigation />
        <main className="flex-1 flex items-center justify-center pb-20 md:pb-8">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <HistoryIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Login Required</h2>
              <p className="text-muted-foreground mb-4">
                Please login to view your prediction history
              </p>
              <Button asChild>
                <a href={getLoginUrl()}>Login</a>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-blue-950/20">
      <Navigation />
      <main className="flex-1 pb-20 md:pb-8">
        <div className="container mx-auto px-4 py-8 space-y-6">
          {/* Latest Draw Display */}
          {latestDraw && (
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Trophy className="w-5 h-5" />
                  Latest Lottery Draw
                </CardTitle>
                <CardDescription>
                  Most recent winning numbers - {latestDraw.drawDate}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col items-center p-4 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30">
                    <div className="text-xs font-semibold text-amber-300 mb-2">1ST PRIZE</div>
                    <div className="text-3xl font-bold text-amber-400 font-mono">{latestDraw.firstPrize}</div>
                  </div>
                  <div className="flex flex-col items-center p-4 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30">
                    <div className="text-xs font-semibold text-blue-300 mb-2">2ND PRIZE</div>
                    <div className="text-3xl font-bold text-blue-400 font-mono">{latestDraw.secondPrize}</div>
                  </div>
                  <div className="flex flex-col items-center p-4 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30">
                    <div className="text-xs font-semibold text-purple-300 mb-2">3RD PRIZE</div>
                    <div className="text-3xl font-bold text-purple-400 font-mono">{latestDraw.thirdPrize}</div>
                  </div>
                </div>
                {latestDraw.color && (
                  <div className="mt-4 text-center text-sm text-muted-foreground">
                    Color: <span className="font-semibold text-primary">{latestDraw.color}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Prediction History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HistoryIcon className="w-5 h-5 text-primary" />
                Prediction History & Results
              </CardTitle>
              <CardDescription>
                Your past predictions with actual draw comparisons and accuracy analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadError ? (
                <div className="text-center py-8">
                  <p className="text-destructive mb-2">Failed to load predictions</p>
                  <p className="text-sm text-muted-foreground">{loadError.message}</p>
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline" 
                    className="mt-4"
                  >
                    Reload Page
                  </Button>
                </div>
              ) : isLoading || authLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : predictionsWithResults && predictionsWithResults.length > 0 ? (
                <div className="space-y-6">
                  {predictionsWithResults.map((pred) => {
                    // Safety checks
                    if (!pred || !pred.numbers || !Array.isArray(pred.numbers)) {
                      console.warn('Invalid prediction data:', pred);
                      return null;
                    }
                    
                    const result = pred.result;
                    let matchedNumbers = [];
                    try {
                      matchedNumbers = result?.matchedNumbers 
                        ? (typeof result.matchedNumbers === 'string' 
                            ? JSON.parse(result.matchedNumbers) 
                            : result.matchedNumbers)
                        : [];
                    } catch (e) {
                      console.error('Failed to parse matchedNumbers:', e);
                      matchedNumbers = [];
                    }
                    
                    const hasResult = result !== null;
                    
                    // Parse the prediction date (it's a timestamp)
                    const predDate = new Date(pred.predictionDate);
                    const formattedDate = predDate.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    });
                    
                    return (
                      <div
                        key={pred.id}
                        className={`p-5 rounded-lg border-2 transition-all ${
                          hasResult
                            ? result.totalMatches > 0
                              ? "bg-gradient-to-br from-green-950/30 to-green-900/20 border-green-700/50"
                              : "bg-gradient-to-br from-secondary/50 to-secondary/30 border-border"
                            : "bg-secondary/50 border-border hover:bg-secondary"
                        }`}
                      >
                        {/* Header: Date, System Type, Accuracy, and Actions */}
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground font-mono">
                              {formattedDate}
                            </span>
                            {pred.systemType && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary/20 text-primary border border-primary/30">
                                {pred.systemType === 'freely' ? '✨ Generate Freely' : 
                                 pred.systemType === 'by_date' ? '📅 Pick by Date' : 
                                 pred.systemType === 'by_number' ? '#️⃣ Pick a Number' : 
                                 '🎯 AI Prediction'}
                              </span>
                            )}
                            {pred.savedForAnalysis && (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                📊 Saved for Analysis
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                              {pred.digitType} digits
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {hasResult && (
                              <div className="flex items-center gap-2 mr-2">
                                {result.totalMatches > 0 ? (
                                  <>
                                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                                    <div className="flex flex-col">
                                      <span className="text-sm font-semibold text-green-400">
                                        {result.totalMatches}/{pred.numbers.length} Matches ({result.accuracyPercent}%)
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {result.exactMatches > 0 && `${result.exactMatches}×4D `}
                                        {matchedNumbers.filter((m: any) => m.matchType === '3digit').length > 0 && `${matchedNumbers.filter((m: any) => m.matchType === '3digit').length}×3D `}
                                        {matchedNumbers.filter((m: any) => m.matchType === '2digit').length > 0 && `${matchedNumbers.filter((m: any) => m.matchType === '2digit').length}×2D`}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-5 h-5 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                      No matches
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                            
                            {/* Share Button */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-2">
                                  <Share2 className="w-4 h-4" />
                                  Share
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleCopyLink(pred.numbers, formattedDate)}>
                                  <LinkIcon className="w-4 h-4 mr-2" />
                                  Copy to Clipboard
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleShareWhatsApp(pred.numbers, formattedDate)}>
                                  <MessageCircle className="w-4 h-4 mr-2" />
                                  Share on WhatsApp
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleShareFacebook(pred.numbers, formattedDate)}>
                                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                  </svg>
                                  Share on Facebook
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleShareTwitter(pred.numbers, formattedDate)}>
                                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                                  </svg>
                                  Share on Twitter
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Delete Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(pred.id)}
                              className="gap-2 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </Button>
                          </div>
                        </div>

                        {/* Your Predictions */}
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            YOUR PREDICTIONS
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(pred.numbers || []).map((num: string, i: number) => {
                              const match = matchedNumbers.find((m: any) => m.number === num);
                              const isMatch = match && match.matchType !== 'none';
                              const isExactMatch = match && match.matchType === 'exact';
                              const is3DigitMatch = match && match.matchType === '3digit';
                              const is2DigitMatch = match && match.matchType === '2digit';
                              
                              return (
                                <div key={i} className="relative">
                                  <span
                                    className={`px-3 py-2 rounded-md font-bold border font-mono text-sm ${
                                      isExactMatch
                                        ? "bg-green-500/30 text-green-300 border-green-500/50"
                                        : is3DigitMatch
                                        ? "bg-blue-500/30 text-blue-300 border-blue-500/50"
                                        : is2DigitMatch
                                        ? "bg-amber-500/30 text-amber-300 border-amber-500/50"
                                        : "bg-primary/20 text-primary border-primary/30"
                                    }`}
                                  >
                                    {num}
                                  </span>
                                  {isMatch && (
                                    <div className="absolute -top-1 -right-1 flex flex-col gap-0.5">
                                      {isExactMatch && (
                                        <div className="px-1 py-0.5 text-[10px] font-bold rounded bg-green-500 text-white">4D</div>
                                      )}
                                      {is3DigitMatch && (
                                        <div className="px-1 py-0.5 text-[10px] font-bold rounded bg-blue-500 text-white">3D</div>
                                      )}
                                      {is2DigitMatch && (
                                        <div className="px-1 py-0.5 text-[10px] font-bold rounded bg-amber-500 text-white">2D</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Actual Draw Results */}
                        {hasResult && result.drawId && (
                          <ActualDrawDisplay drawDate={result.drawDate} matchedNumbers={matchedNumbers} />
                        )}

                        {/* Date Combinations Section — only for by_date predictions */}
                        {pred.systemType === 'by_date' && pred.dateCombinations && Array.isArray(pred.dateCombinations) && pred.dateCombinations.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="w-3.5 h-3.5 text-blue-400" />
                              <span className="text-xs font-semibold text-blue-300">DATE COMBINATIONS</span>
                              <span className="text-xs text-muted-foreground">({pred.dateCombinations.length} numbers from {pred.dateInput || 'date'})</span>
                              <div className="ml-auto flex items-center gap-1.5">
                                <button
                                  onClick={() => setExpandedDateCombosId(expandedDateCombosId === pred.id ? null : pred.id)}
                                  className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
                                >
                                  {expandedDateCombosId === pred.id ? 'Hide' : 'Show all'}
                                </button>
                                <button
                                  onClick={() => handleCopyDateCombinations(pred.id, pred.dateCombinations as any[])}
                                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-all ${
                                    copiedDateCombosId === pred.id
                                      ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                                      : 'bg-blue-500/20 text-blue-300 border border-blue-500/40 hover:bg-blue-500/30'
                                  }`}
                                >
                                  {copiedDateCombosId === pred.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                  {copiedDateCombosId === pred.id ? 'Copied!' : 'Copy All'}
                                </button>
                              </div>
                            </div>
                            {expandedDateCombosId === pred.id && (
                              <div className="mt-2 p-3 rounded-lg bg-blue-950/30 border border-blue-500/20">
                                {(() => {
                                  const groupOrder = ['Direct Date', 'Age-Based', 'Arithmetic', 'Mirror', 'Other'];
                                  const groups: Record<string, any[]> = { 'Direct Date': [], 'Age-Based': [], 'Arithmetic': [], 'Mirror': [], 'Other': [] };
                                  (pred.dateCombinations as any[]).forEach((c: any) => {
                                    if (c.group === 'age') groups['Age-Based'].push(c);
                                    else if (c.group === 'mirror') groups['Mirror'].push(c);
                                    else if (c.group === 'arithmetic') groups['Arithmetic'].push(c);
                                    else if (c.group === 'direct') groups['Direct Date'].push(c);
                                    else groups['Other'].push(c);
                                  });
                                  return groupOrder.filter(g => groups[g].length > 0).map(groupName => (
                                    <div key={groupName} className="mb-3 last:mb-0">
                                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{groupName}</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {groups[groupName].map((c: any, i: number) => (
                                          <div key={i} className="flex flex-col items-center bg-slate-800/70 border border-blue-500/20 rounded px-2 py-1 min-w-[52px]">
                                            <span className="text-sm font-bold text-white tracking-widest">{c.number}</span>
                                            <span className="text-[9px] text-blue-400 mt-0.5 text-center leading-tight">{c.label}</span>
                                            {c.frequency > 0 ? (
                                              <span className="text-[9px] text-green-400">{c.frequency}x</span>
                                            ) : (
                                              <span className="text-[9px] text-gray-600">new</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}
                            {expandedDateCombosId !== pred.id && (
                              <div className="flex flex-wrap gap-1.5">
                                {(pred.dateCombinations as any[]).slice(0, 8).map((c: any, i: number) => (
                                  <span key={i} className="px-2 py-1 rounded bg-blue-900/40 border border-blue-500/20 text-xs font-bold text-blue-200 font-mono">
                                    {c.number}
                                  </span>
                                ))}
                                {pred.dateCombinations.length > 8 && (
                                  <span className="px-2 py-1 text-xs text-muted-foreground">+{pred.dateCombinations.length - 8} more</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pending Evaluation */}
                        {!hasResult && (
                          <div className="pt-4 border-t border-border/50">
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></div>
                              Waiting for draw on {pred.targetDate} to evaluate results
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No predictions yet. Generate your first prediction!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prediction?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this prediction and its results from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Component to fetch and display actual draw
function ActualDrawDisplay({ drawDate, matchedNumbers }: { drawDate: string; matchedNumbers: any[] }) {
  const { data: drawData } = trpc.lottery.getDrawByDate.useQuery({ date: drawDate });
  
  if (!drawData) {
    return (
      <div className="pt-4 border-t border-border/50">
        <div className="text-xs font-semibold text-muted-foreground mb-2">
          ACTUAL DRAW ({drawDate})
        </div>
        <div className="text-sm text-muted-foreground">Loading draw data...</div>
      </div>
    );
  }

  const hasMatches = matchedNumbers.some((m: any) => m.matchType !== 'none');

  return (
    <div className="pt-4 border-t border-border/50">
      <div className="text-xs font-semibold text-muted-foreground mb-3">
        ACTUAL DRAW ({drawDate})
      </div>
      
      {/* Show the actual winning numbers */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="flex flex-col items-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <div className="text-xs text-amber-300 mb-1">1st Prize</div>
          <div className="text-lg font-bold text-amber-400 font-mono">{drawData.firstPrize}</div>
        </div>
        <div className="flex flex-col items-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <div className="text-xs text-blue-300 mb-1">2nd Prize</div>
          <div className="text-lg font-bold text-blue-400 font-mono">{drawData.secondPrize}</div>
        </div>
        <div className="flex flex-col items-center p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <div className="text-xs text-purple-300 mb-1">3rd Prize</div>
          <div className="text-lg font-bold text-purple-400 font-mono">{drawData.thirdPrize}</div>
        </div>
      </div>

      {/* Show match details */}
      {hasMatches ? (
        <div className="space-y-1">
          {matchedNumbers
            .filter((m: any) => m.matchType !== 'none')
            .map((m: any, idx: number) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary font-mono">
                  {m.number}
                </span>
                <span className="text-xs text-muted-foreground">
                  matched in position {m.actualPosition}
                  {m.matchType === 'exact' && ' (exact position!)'}
                </span>
              </div>
            ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          None of your predicted numbers appeared in this draw
        </div>
      )}
    </div>
  );
}
